/** Vista della chiamata: video remoto grande, self-preview piccolo, controlli. */
import {
  Maximize2,
  Mic,
  MicOff,
  Minimize2,
  Monitor,
  MonitorOff,
  PhoneOff,
  ShieldAlert,
  Video,
  VideoOff,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useSupport } from "./SupportContext";

function useStream(stream: MediaStream | null) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream;
  }, [stream]);
  return ref;
}

const controlButton =
  "flex items-center gap-2 rounded-lg bg-surface px-4 py-2.5 text-sm transition-colors hover:bg-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2";

export function CallView() {
  const { localStream, remoteStream, micOn, camOn, screenOn, setMic, setCam, toggleScreen, hangup } =
    useSupport();
  const remoteRef = useStream(remoteStream);
  const localRef = useStream(localStream);
  const containerRef = useRef<HTMLDivElement>(null);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    const onFullscreenChange = () => {
      setFullscreen(document.fullscreenElement === containerRef.current);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => {});
      return;
    }
    // requestFullscreen() rifiuta la Promise se il browser nega il fullscreen
    // (es. gesto utente non riconosciuto): la vista resta quella normale, nessun crash.
    void containerRef.current?.requestFullscreen().catch(() => {});
  };

  return (
    <div className="flex flex-col gap-3">
      <div ref={containerRef} className="relative overflow-hidden rounded-xl bg-black">
        <video
          ref={remoteRef}
          autoPlay
          playsInline
          // L'audio esce dall'<audio> del provider, che sopravvive alla navigazione:
          // senza muted qui si sentirebbe due volte, sfasato.
          muted
          aria-label="Video del supervisore"
          className="h-[70dvh] w-full object-contain"
        />
        <video
          ref={localRef}
          autoPlay
          playsInline
          muted
          aria-label="Anteprima della tua webcam"
          className="absolute bottom-3 right-3 h-28 w-40 rounded-lg border border-white/20 object-cover"
        />
        <p
          role="status"
          className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-black/80 px-3 py-1.5 text-xs font-medium text-white"
        >
          <ShieldAlert className="h-3.5 w-3.5" aria-hidden="true" />
          Sessione live, non registrata
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2">
        <button type="button" onClick={() => setMic(!micOn)} className={controlButton}>
          {micOn ? <Mic className="h-4 w-4" aria-hidden="true" /> : <MicOff className="h-4 w-4" aria-hidden="true" />}
          {micOn ? "Microfono attivo" : "Microfono muto"}
        </button>
        <button type="button" onClick={() => setCam(!camOn)} className={controlButton}>
          {camOn ? <Video className="h-4 w-4" aria-hidden="true" /> : <VideoOff className="h-4 w-4" aria-hidden="true" />}
          {camOn ? "Webcam attiva" : "Webcam spenta"}
        </button>
        <button type="button" onClick={() => void toggleScreen()} className={controlButton}>
          {screenOn ? (
            <MonitorOff className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Monitor className="h-4 w-4" aria-hidden="true" />
          )}
          {screenOn ? "Ferma condivisione schermo" : "Condividi schermo"}
        </button>
        <button type="button" onClick={toggleFullscreen} className={controlButton}>
          {fullscreen ? (
            <Minimize2 className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Maximize2 className="h-4 w-4" aria-hidden="true" />
          )}
          {fullscreen ? "Esci da schermo intero" : "Schermo intero"}
        </button>
        <button
          type="button"
          onClick={hangup}
          className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2"
        >
          <PhoneOff className="h-4 w-4" aria-hidden="true" />
          Riaggancia
        </button>
      </div>

      <p className="text-center text-xs text-muted-foreground">
        Condividendo lo schermo mostri solo la finestra che scegli tu. La sessione non viene
        registrata.
      </p>
    </div>
  );
}
